import React from 'react';
import PropTypes from 'prop-types';
import { ChevronDown, ChevronRight, HelpCircle } from 'lucide-react';

/**
 * CategoryCard - Displays a single category with expandable questions
 * @param {string} categoryKey - Category key identifier
 * @param {string} categoryName - Display name for the category
 * @param {number} score - Category score percentage
 * @param {Array} questions - Array of questions for this category
 * @param {boolean} isExpanded - Whether the category is expanded
 * @param {function} onToggle - Handler to toggle category expansion
 * @param {function} onQuestionEdit - Handler when a question is clicked
 * @param {function} onShowHelp - Handler to show category help modal
 * @param {function} getAnswer - Function to get answer for a question
 * @param {Object} assessmentType - Assessment type info (label, color) or null
 * @param {boolean} isFirst - Whether this is the first in its group
 * @param {number} animationKey - Animation key for triggering progress bars
 * @param {boolean} viewingHistoricalAssessment - Whether viewing historical assessment
 * @param {boolean} showFullQuestionText - Whether to show full question text
 * @param {string|null} selectedGapFilter - Currently active gap filter
 * @param {function} getCategoryBackgroundColor - Function to get category background color
 * @param {function} getScoreTextColor - Function to get score text color
 * @param {function} getScoreColor - Function to get score bar color
 */
const CategoryCard = ({
  categoryKey,
  categoryName,
  score,
  questions,
  isExpanded,
  onToggle,
  onQuestionEdit,
  onShowHelp,
  getAnswer,
  assessmentType,
  isFirst,
  animationKey,
  viewingHistoricalAssessment,
  showFullQuestionText,
  selectedGapFilter,
  getCategoryBackgroundColor,
  getScoreTextColor,
  getScoreColor
}) => {
  /**
   * Gets gap zone label for a score
   * @param {number} score - Score percentage
   * @returns {string} Gap zone label
   */
  const getGapZoneLabel = (score) => {
    if (score > 86) return 'No Gaps';
    if (score > 72) return 'Minor Gaps';
    if (score > 57) return 'Considerable Gaps';
    if (score > 43) return 'Critical Gaps';
    if (score > 28) return 'Very Critical';
    return 'Extremely Critical';
  };

  return (
    <div className="flex items-stretch gap-0">
      {/* Left bracket indicator - Hidden on mobile */}
      <div className="hidden sm:flex flex-col items-center" style={{ width: '40px' }}>
        {isFirst && assessmentType && (
          <div
            className={`text-xs font-semibold px-1 py-0.5 rounded-t ${assessmentType.color} text-center whitespace-nowrap -rotate-90 origin-center`}
            style={{ marginTop: '60px', minWidth: '120px' }}
          >
            {assessmentType.label}
          </div>
        )}
        <div className={`flex-1 w-1 ${assessmentType ? 'border-l-4 ' + assessmentType.color.split(' ')[1] : ''}`}></div>
      </div>

      {/* Category card */}
      <div className="flex-1 bg-white rounded-lg border-2 border-gray-300 overflow-hidden shadow-sm">
        {/* Mobile assessment type badge - Shown only on mobile for first card */}
        {isFirst && assessmentType && (
          <div className="sm:hidden px-3 py-1.5 border-b border-gray-200">
            <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-semibold ${assessmentType.color}`}>
              {assessmentType.label}
            </span>
          </div>
        )}
        {/* Category Header */}
        <div className={`w-full p-3 sm:p-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-0 sm:justify-between ${getCategoryBackgroundColor(score)}`}>
          {/* Left side - Expand/Collapse button with category name */}
          <button
            onClick={onToggle}
            className="flex items-center space-x-3 hover:opacity-80 transition flex-1 min-h-[44px]"
          >
            {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
            <span className="font-semibold text-sm sm:text-base text-gray-900">{categoryName}</span>
          </button>

          {/* Right side - Help icon, score, and progress bar */}
          <div className="flex items-center space-x-3 justify-end">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onShowHelp(categoryKey);
              }}
              className="text-gray-600 hover:text-blue-600 transition p-1"
              title={`Learn more about ${categoryName}`}
            >
              <HelpCircle size={18} />
            </button>
            <span className={`text-lg font-bold ${getScoreTextColor(score)}`}>
              {Math.round(score)}%
            </span>
            <div className="w-24 bg-gray-200 rounded-full h-2">
              <div
                key={`cat-${categoryKey}-${animationKey}`}
                className="h-full rounded-full"
                style={{
                  width: 0,
                  backgroundColor: getScoreColor(score),
                  animation: `growWidth 3500ms cubic-bezier(0.4, 0, 0.2, 1) forwards`,
                  animationFillMode: 'forwards',
                  '--final-width': `${score}%`
                }}
              ></div>
            </div>
          </div>
        </div>

        {/* Expanded Questions List */}
        {isExpanded && (
          <div className="border-t border-gray-200 bg-gray-50">
            {questions.length > 0 ? (
              <div className="p-3 space-y-1">
                {questions.map((question) => {
                  const answer = getAnswer(question.question_id);
                  return (
                    <div key={question.question_id}>
                      <div
                        onClick={() => onQuestionEdit(question, answer)}
                        className={`px-3 py-2 rounded border transition cursor-pointer ${
                          answer && answer.answer_value === 0
                            ? 'bg-gray-50 border-gray-200 opacity-60'
                            : viewingHistoricalAssessment
                            ? 'bg-white border-gray-200 hover:border-amber-400 hover:bg-amber-50'
                            : 'bg-white border-gray-200 hover:border-blue-400 hover:bg-blue-50'
                        }`}
                      >
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                          <p className={`text-sm font-medium w-full sm:min-w-[200px] sm:flex-1 ${
                            answer && answer.answer_value === 0 ? 'text-gray-400' : 'text-gray-900'
                          }`}>
                            {question.subject || 'Question'}
                          </p>
                          {answer && (
                            <>
                              {answer.answer_value === 0 ? (
                                // N/A Display
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-300 self-start">
                                  N/A
                                </span>
                              ) : (
                                // Normal Score Display - Stack on mobile, inline on desktop
                                <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto sm:flex-1">
                                  <div className="flex-1 sm:flex-initial sm:min-w-[100px] bg-gray-200 rounded-full h-2">
                                    <div
                                      key={`q-${question.question_id}-${animationKey}`}
                                      className="h-full rounded-full"
                                      style={{
                                        width: 0,
                                        backgroundColor: getScoreColor(answer.score),
                                        animation: `growWidth 3500ms cubic-bezier(0.4, 0, 0.2, 1) forwards`,
                                        animationFillMode: 'forwards',
                                        '--final-width': `${answer.score}%`
                                      }}
                                    ></div>
                                  </div>
                                  <span className={`text-xs font-semibold whitespace-nowrap w-auto sm:min-w-[120px] text-right ${getScoreTextColor(answer.score)}`}>
                                    {getGapZoneLabel(answer.score)}
                                  </span>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                      {showFullQuestionText && (
                        <p className={`text-xs mt-1 px-3 ${
                          answer && answer.answer_value === 0 ? 'text-gray-400 line-through' : 'text-gray-500'
                        }`}>
                          {question.question_text}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="p-4 text-center text-gray-500 text-sm">
                {selectedGapFilter
                  ? 'No questions match the selected filter in this category'
                  : 'No questions answered in this category yet'}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

CategoryCard.propTypes = {
  categoryKey: PropTypes.string.isRequired,
  categoryName: PropTypes.string.isRequired,
  score: PropTypes.number.isRequired,
  questions: PropTypes.arrayOf(PropTypes.shape({
    question_id: PropTypes.string.isRequired,
    subject: PropTypes.string,
    question_text: PropTypes.string
  })).isRequired,
  isExpanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  onQuestionEdit: PropTypes.func.isRequired,
  onShowHelp: PropTypes.func.isRequired,
  getAnswer: PropTypes.func.isRequired,
  assessmentType: PropTypes.shape({
    label: PropTypes.string,
    color: PropTypes.string
  }),
  isFirst: PropTypes.bool.isRequired,
  animationKey: PropTypes.number.isRequired,
  viewingHistoricalAssessment: PropTypes.bool.isRequired,
  showFullQuestionText: PropTypes.bool.isRequired,
  selectedGapFilter: PropTypes.string,
  getCategoryBackgroundColor: PropTypes.func.isRequired,
  getScoreTextColor: PropTypes.func.isRequired,
  getScoreColor: PropTypes.func.isRequired
};

export default CategoryCard;
